import { useState, useEffect } from "react";

const C = {
  bg:      "#081327",
  bgLow:   "#111b30",
  bgMid:   "#151f34",
  bgHigh:  "#202a3f",
  bgTop:   "#2a354a",
  gold:    "#f2c567",
  goldMid: "#d4aa4f",
  text:    "#d8e2fe",
  textDim: "#d1c5b2",
  textMute:"#6b7a99",
  error:   "#ffb4ab",
  border:  "rgba(78,70,55,0.2)",
};

function useMarketData(selectedDate) {
  const [market, setMarket] = useState(null);
  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const url = selectedDate
          ? 'https://zhongou-fund-daily-market-update.oss-cn-shanghai.aliyuncs.com/data/history/' + selectedDate + '.json'
          : 'https://zhongou-fund-daily-market-update.oss-cn-shanghai.aliyuncs.com/data/market_overview.json';
        const r = await fetch(url);
        if (!r.ok) return;
        const j = await r.json();
        if (!c) setMarket(j);
      } catch (e) {}
    })();
    return () => { c = true; };
  }, [selectedDate]);
  return market;
}

function useFundsData() {
  const [funds, setFunds] = useState([]);
  const [meta, setMeta] = useState({ updated_at: "", source: "Wind" });
  useEffect(() => {
    let c = false;
    fetch('https://zhongou-fund-daily-market-update.oss-cn-shanghai.aliyuncs.com/data/funds_wind.json')
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (!j || c) return;
        const processed = (j.funds || []).filter(f => f.code && f.code.endsWith('.OF'));
        setFunds(processed);
        setMeta({ updated_at: j.updated_at, source: "Wind" });
      })
      .catch(() => {});
    return () => { c = true; };
  }, []);
  return { funds, meta };
}

function useDailySummary() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    fetch('https://zhongou-fund-daily-market-update.oss-cn-shanghai.aliyuncs.com/data/daily_summary.json')
      .then(r => r.ok ? r.json() : null)
      .then(j => j && setSummary(j))
      .catch(() => {});
  }, []);
  return summary;
}

function useAvailableDates() {
  const [dates, setDates] = useState([]);
  useEffect(() => {
    const base = 'https://zhongou-fund-daily-market-update.oss-cn-shanghai.aliyuncs.com/data/history/';
    const found = [];
    const checks = [];
    for (let i = 1; i <= 30; i++) {
      const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      checks.push(fetch(base + d + '.json', { method: 'HEAD' }).then(r => r.ok && found.push(d)).catch(() => {}));
    }
    Promise.all(checks).then(() => setDates([...found].sort().reverse()));
  }, []);
  return dates;
}

const fmtPct = (v) => v == null ? "—" : (v > 0 ? "+" : "") + v.toFixed(2) + "%";
const pctColor = (v) => v == null ? C.textMute : v > 0 ? C.gold : C.error;
const shortName = (n) => n.replace(/混合型?[A-Za-z]?$/, "").replace(/股票型?[A-Za-z]?$/, "").replace(/债券型?[A-Za-z]?$/, "").slice(0, 12);

const METRICS = [
  { key: 'change_pct', label: '今日' },
  { key: 'return_ytd', label: '今年以来' },
  { key: 'return_1m',  label: '近1月' },
  { key: 'return_3m',  label: '近3月' },
  { key: 'return_6m',  label: '近6月' },
  { key: 'return_1y',  label: '近1年' },
  { key: 'return_3y',  label: '近3年' },
];

const SECTION_CONFIG = {
  a_share:  { label: 'A股板块',  icon: '🇨🇳' },
  hk_share: { label: '港股板块', icon: '🇭🇰' },
  us_stocks:{ label: '美股板块', icon: '🇺🇸' },
  precious: { label: '贵金属',   icon: '🥇' },
  energy:   { label: '能源化工', icon: '⛽' },
  metals:   { label: '基本金属', icon: '🔩' },
};

function MetricTabs({ metric, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {METRICS.map(m => (
        <button key={m.key} onClick={() => onChange(m.key)} style={{
          padding: "5px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer",
          fontWeight: 600, letterSpacing: "0.03em", transition: "all 0.2s",
          border: metric === m.key ? "1px solid " + C.gold : "1px solid " + C.border,
          background: metric === m.key ? "rgba(242,197,103,0.12)" : "transparent",
          color: metric === m.key ? C.gold : C.textDim,
          fontFamily: "inherit",
        }}>{m.label}</button>
      ))}
    </div>
  );
}

function SectionCard({ sec, metric }) {
  const cfg = SECTION_CONFIG[sec.id] || { label: sec.title, icon: "📊" };
  const items = [...sec.items]
    .filter(i => i.code !== '--' && i.name !== '数据更新中' && i[metric] != null)
    .sort((a, b) => (b[metric] || 0) - (a[metric] || 0))
    .slice(0, 5);

  return (
    <div style={{
      background: C.bgLow, borderRadius: 8,
      boxShadow: "0 8px 32px rgba(4,14,34,0.4)",
      overflow: "hidden", display: "flex", flexDirection: "column",
    }}>
      <div style={{
        padding: "12px 16px", display: "flex", alignItems: "center", gap: 8,
        borderBottom: "1px solid " + C.border,
      }}>
        <span style={{ fontSize: 14 }}>{cfg.icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", color: C.text, textTransform: "uppercase" }}>
          {cfg.label}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: C.textMute, fontWeight: 600 }}>TOP 5</span>
      </div>
      <div style={{ padding: "4px 0" }}>
        {items.length === 0 ? (
          <div style={{ padding: "16px", textAlign: "center", color: C.textMute, fontSize: 11 }}>暂无数据</div>
        ) : items.map((item, i) => (
          <div key={item.code} style={{
            display: "flex", alignItems: "center", padding: "0 16px", gap: 8,
            background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)",
            minHeight: 52,
          }}>
            <span style={{ fontSize: 10, color: C.textMute, width: 14, textAlign: "center", fontWeight: 700, flexShrink: 0 }}>
              {i + 1}
            </span>
            <span style={{
              fontSize: 13, color: C.text, flex: 1,
              overflow: "hidden", display: "-webkit-box",
              WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              lineHeight: 1.4, wordBreak: "break-all",
            }}>{item.name}</span>
            <span style={{
              fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums",
              color: pctColor(item[metric]), minWidth: 64, textAlign: "right", flexShrink: 0,
            }}>
              {fmtPct(item[metric])}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FundCard({ funds, metric }) {
  const sortKey = metric || 'return_ytd';
  const top5 = [...funds]
    .filter(f => f[sortKey] != null)
    .sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0))
    .slice(0, 5);
  const label = METRICS.find(m => m.key === sortKey)?.label || "今年以来";

  return (
    <div style={{
      background: C.bgLow, borderRadius: 8,
      boxShadow: "0 8px 32px rgba(4,14,34,0.4)",
      overflow: "hidden", display: "flex", flexDirection: "column",
    }}>
      <div style={{
        padding: "12px 16px", display: "flex", alignItems: "center", gap: 8,
        borderBottom: "1px solid " + C.border,
        background: "linear-gradient(135deg, rgba(242,197,103,0.08), transparent)",
      }}>
        <span style={{ fontSize: 14 }}>💹</span>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", color: C.gold, textTransform: "uppercase" }}>
          基金 TOP5
        </span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: C.textMute, fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ padding: "4px 0" }}>
        {top5.map((f, i) => (
          <div key={f.code} style={{
            display: "flex", alignItems: "center", padding: "0 16px", gap: 8,
            background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)",
            minHeight: 52,
          }}>
            <span style={{ fontSize: 10, color: C.textMute, width: 14, textAlign: "center", fontWeight: 700, flexShrink: 0 }}>
              {i + 1}
            </span>
            <div style={{ flex: 1, overflow: "hidden" }}>
              <div style={{
                fontSize: 12, color: C.text,
                overflow: "hidden", display: "-webkit-box",
                WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                lineHeight: 1.4, wordBreak: "break-all",
              }}>
                {shortName(f.name)}
              </div>
              <div style={{ fontSize: 11, color: C.textMute, marginTop: 2 }}>{f.code}</div>
            </div>
            <span style={{
              fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums",
              color: pctColor(f[sortKey]), minWidth: 60, textAlign: "right", flexShrink: 0,
            }}>
              {fmtPct(f[sortKey])}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ summary }) {
  if (!summary) return null;
  return (
    <div style={{
      background: C.bgLow, borderRadius: 8,
      boxShadow: "0 8px 32px rgba(4,14,34,0.4)",
      overflow: "hidden", position: "relative",
    }}>
      <div style={{
        position: "absolute", right: -40, top: -40, width: 120, height: 120,
        background: "rgba(242,197,103,0.04)", borderRadius: "50%", filter: "blur(30px)",
      }} />
      <div style={{
        padding: "12px 16px", display: "flex", alignItems: "center", gap: 8,
        borderBottom: "1px solid " + C.border,
      }}>
        <span style={{ fontSize: 14 }}>📋</span>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", color: C.text, textTransform: "uppercase" }}>
          每日市场速览
        </span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: C.textMute }}>{summary.date}</span>
      </div>
      <div style={{
        padding: "16px 20px", fontSize: 12, lineHeight: 2,
        color: C.textDim, whiteSpace: "pre-wrap", position: "relative", zIndex: 1,
      }}>
        {summary.content}
      </div>
    </div>
  );
}

function TickerBar({ data, metric }) {
  if (!data || !data.sections) return null;
  const tickers = [];
  data.sections.forEach(sec => {
    const top1 = [...sec.items]
      .filter(i => i.code !== '--' && i[metric] != null)
      .sort((a, b) => (b[metric] || 0) - (a[metric] || 0))[0];
    if (top1) tickers.push({ section: SECTION_CONFIG[sec.id]?.label || sec.title, item: top1 });
  });

  return (
    <div style={{
      background: C.bgLow, borderBottom: "1px solid " + C.border,
      padding: "6px 24px", display: "flex", gap: 32, overflowX: "auto",
      scrollbarWidth: "none",
    }}>
      {tickers.map((t, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
          <span style={{ fontSize: 10, color: C.textMute, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {t.section}
          </span>
          <span style={{ fontSize: 11, color: C.text, fontWeight: 500 }}>{t.item.name}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: pctColor(t.item[metric]) }}>
            {fmtPct(t.item[metric])}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function App() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [metric, setMetric] = useState('change_pct');
  const market = useMarketData(selectedDate);
  const { funds, meta } = useFundsData();
  const summary = useDailySummary();
  const availableDates = useAvailableDates();

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'Manrope', -apple-system, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* 顶部导航 */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        background: C.bgLow, borderBottom: "1px solid " + C.border,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 20px", height: 56,
        boxShadow: "0 4px 24px rgba(4,14,34,0.3)",
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{
            fontSize: 16, fontWeight: 800, letterSpacing: "0.02em",
            background: "linear-gradient(135deg, " + C.gold + ", " + C.goldMid + ")",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>全球市场行情</span>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: C.textMute, textTransform: "uppercase" }}>
            MARKET TERMINAL
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {meta.updated_at && (
            <div style={{ textAlign: "right", display: "none", }} className="desktop-meta">
              <div style={{ fontSize: 10, color: C.textMute }}>数据更新</div>
              <div style={{ fontSize: 10, color: C.textDim, fontWeight: 600 }}>{meta.updated_at}</div>
            </div>
          )}
          {availableDates.length > 0 && (
            <select value={selectedDate || ''} onChange={e => setSelectedDate(e.target.value || null)} style={{
              padding: "6px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer",
              background: C.bgHigh, color: C.text, border: "1px solid " + C.border,
              fontFamily: "inherit",
            }}>
              <option value=''>最新数据</option>
              {availableDates.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
        </div>
      </header>

      {/* 行情条 */}
      {market && <TickerBar data={market} metric={metric} />}

      {/* 主内容 */}
      <main style={{ maxWidth: 1400, margin: "0 auto", padding: "20px 16px 48px" }}>

        {/* 指标切换区 */}
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          marginBottom: 16, flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: "0.02em" }}>市场概览</div>
            {market?.updated_at && (
              <div style={{ fontSize: 10, color: C.textMute, marginTop: 2 }}>{market.updated_at}</div>
            )}
          </div>
          <MetricTabs metric={metric} onChange={setMetric} />
        </div>

        {/* 板块网格 */}
        {market && market.sections ? (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(340px, 100%), 1fr))",
            gap: 12, marginBottom: 16, alignItems: "stretch",
          }}>
            {market.sections.map(sec => (
              <SectionCard key={sec.id} sec={sec} metric={metric} />
            ))}
            <FundCard funds={funds} metric={metric} />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "60px 0", color: C.textMute, fontSize: 13 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
            数据加载中...
          </div>
        )}

        {/* 每日速览 */}
        <SummaryCard summary={summary} />
      </main>

      {/* 页脚 */}
      <footer style={{
        borderTop: "1px solid " + C.border, padding: "12px 24px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: C.bgLow, flexWrap: "wrap", gap: 8,
      }}>
        <span style={{ fontSize: 10, color: C.textMute }}>数据来源：Wind · 仅供内部研究参考，不构成投资建议</span>
        <span style={{ fontSize: 10, color: C.textMute }}>© 2026</span>
      </footer>
    </div>
  );
}
